import { formatBrandName } from "@/features/dashboard/lib/format"

import { drilldownCompetitorFolder, extractNumber, extractString, resolveDrilldownCompetitor } from "./drilldown-row"
import { SEGMENT_NONE } from "../stores/drilldownFilters.store"

export const DRILLDOWN_CSV_MIME = "text/csv;charset=utf-8"

/** Retool `utils.exportData(..., "drilldown_export.csv", "csv")` — not dated. */
export const DRILLDOWN_EXPORT_FILENAME = "drilldown_export.csv"

/** So Excel (locale list-separator `;`) still splits on commas when the file is double-clicked. */
const EXCEL_SEP_LINE = "sep=,"

const UTF8_BOM = "\uFEFF"

const ASSET_URL_COLUMN = "asset_url"

/** Competitors only — mirrors the List view's Brand column. */
const BRAND_COLUMN = "Brand"

const DATE_COLUMNS = ["min_date", "max_date"] as const

const COMPETITOR_METRIC_COLUMNS = ["Days", "Assets", "DaysPerAsset"] as const

const GROUPBY_FIELDS = ["Groupby1", "Groupby2", "Groupby3"] as const

/** Characters that make a spreadsheet treat a cell as a formula (CSV injection). */
const FORMULA_PREFIXES = ["=", "+", "-", "@"]

export interface DrilldownExportKpi {
  /** Canonical `Kpi.name` — how Nexus rows store the value. */
  id: string
  /** `Kpi.displayName` — Retool CSV header (`Display_Name`). */
  label: string
}

export interface BuildDrilldownExportCsvInput {
  rows: Array<Record<string, unknown>>
  mode: "my-brand" | "competitors"
  /** Applied segment selector values (`Hash`, `Ad ID`, …). Inactive slots are `All` / `''`. */
  segments: readonly [string, string, string]
  /** Visible drilldown catalog in Index order. Ignored for Competitors. */
  kpis: readonly DrilldownExportKpi[]
  /** Saved competitors path. Defaults to the public pub_data prefix. */
  competitorsPath?: string
}

interface Column {
  header: string
  cell: (row: Record<string, unknown>) => string
}

/**
 * Prefix formula-leading text so Excel/Sheets does not execute it. Numeric cells
 * skip this — a legitimate `-12.5` must stay a number, not `'-12.5`.
 */
function neutralizeFormula(value: string): string {
  const clean = value.replace(/[\n\r]/g, " ").trim()
  return FORMULA_PREFIXES.some((prefix) => clean.startsWith(prefix)) ? `'${clean}` : clean
}

/**
 * Always quote fields. An unquoted `https://…png,2026-06-11,…` line is treated as
 * one URL by browsers and by Excel when the locale list-separator is `;`, so GCS
 * looks up `thumbnails/<hash>.png,<dates>,<metrics>` and returns NoSuchKey.
 */
function escapeCsvField(value: string): string {
  const clean = value.replace(/[\n\r]/g, " ").trim()
  return `"${clean.replace(/"/g, '""')}"`
}

function unwrapCell(val: unknown): unknown {
  if (val !== null && typeof val === "object" && "value" in val) {
    return (val as { value: unknown }).value
  }
  return val
}

/** Raw numbers for spreadsheets; empty cell for missing/NaN. Strings get formula-guarded. */
function csvCell(val: unknown): string {
  const inner = unwrapCell(val)
  if (inner === null || inner === undefined) return ""
  if (typeof inner === "string" && inner.trim() === "") return ""
  const n = extractNumber(inner)
  if (n !== null) return String(n)
  if (typeof inner === "number") return ""
  return neutralizeFormula(extractString(inner))
}

function isActiveSegment(value: string): boolean {
  return value !== "" && value !== SEGMENT_NONE
}

/** Matches list/card/chart views: video is any non-image, non-carousel object_type, including blank. */
function isVideoCreative(row: Record<string, unknown>): boolean {
  const objectType = extractString(row.object_type).toLowerCase()
  return objectType !== "image" && objectType !== "carousel"
}

/** Always the full asset file, never the `/thumbnails/` preview. */
function competitorAssetUrl(
  row: Record<string, unknown>,
  competitorsPath?: string,
): string {
  const hash = extractString(row.hash)
  const competitor = resolveDrilldownCompetitor(extractString(row.competitor))
  const folder = drilldownCompetitorFolder(competitor, competitorsPath)
  if (!hash || !folder) return ""
  const ext = isVideoCreative(row) ? "mp4" : "png"
  return `${folder}/assets/${hash}.${ext}`
}

function assetUrlCell(
  mode: "my-brand" | "competitors",
  row: Record<string, unknown>,
  competitorsPath?: string,
): string {
  return mode === "competitors"
    ? competitorAssetUrl(row, competitorsPath)
    : extractString(row.creative_url)
}

function brandCell(row: Record<string, unknown>): string {
  const competitor = resolveDrilldownCompetitor(extractString(row.competitor))
  return competitor ? neutralizeFormula(formatBrandName(competitor)) : ""
}

function kpiCell(row: Record<string, unknown>, kpi: DrilldownExportKpi): string {
  if (Object.prototype.hasOwnProperty.call(row, kpi.id)) return csvCell(row[kpi.id])
  if (kpi.label !== kpi.id && Object.prototype.hasOwnProperty.call(row, kpi.label)) {
    return csvCell(row[kpi.label])
  }
  return ""
}

function buildColumns(input: BuildDrilldownExportCsvInput): Column[] {
  const { mode, segments, kpis, competitorsPath } = input
  const columns: Column[] = [
    {
      header: ASSET_URL_COLUMN,
      cell: (row) => neutralizeFormula(assetUrlCell(mode, row, competitorsPath)),
    },
  ]
  if (mode === "competitors") {
    columns.push({ header: BRAND_COLUMN, cell: brandCell })
  }
  for (const field of DATE_COLUMNS) {
    columns.push({ header: field, cell: (row) => csvCell(row[field]) })
  }

  segments.forEach((segment, index) => {
    if (!isActiveSegment(segment)) return
    const field = GROUPBY_FIELDS[index]
    columns.push({ header: segment, cell: (row) => csvCell(row[field]) })
  })

  if (mode === "my-brand") {
    for (const kpi of kpis) {
      columns.push({ header: kpi.label, cell: (row) => kpiCell(row, kpi) })
    }
  } else {
    for (const metric of COMPETITOR_METRIC_COLUMNS) {
      columns.push({ header: metric, cell: (row) => csvCell(row[metric]) })
    }
  }

  return columns
}

/**
 * Serialize loaded drilldown rows as CSV — Retool `downloadDrilldownData` column shape
 * for My Brand, except the link column is `asset_url` and always points at `/assets/`.
 * Competitors adds Brand after the link, then the same date/segment shape plus Days/Assets.
 */
export function buildDrilldownExportCsv(input: BuildDrilldownExportCsvInput): string {
  const columns = buildColumns(input)
  const header = columns.map((column) => escapeCsvField(neutralizeFormula(column.header))).join(",")
  const lines = input.rows.map((row) =>
    columns.map((column) => escapeCsvField(column.cell(row))).join(","),
  )
  const body = [header, ...lines].join("\r\n")
  return `${UTF8_BOM}${EXCEL_SEP_LINE}\r\n${body}`
}
